import { useState } from "react";
import { Download, CheckCircle2, FileText, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BENEFITS = [
  "The exact 7-page structure used in every Lead Gen Website",
  "Copy framework that turns visitors into booked appointments",
  "Local SEO checklist: what to do, what to skip",
  "Conversion checkpoints — where most websites lose the customer",
];

export default function LeadMagnet() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/subscribe", { email, source: "lead_magnet_playbook" });
      setSent(true);
      toast.success("Sent. Check your inbox in a few minutes.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="lead-magnet-section" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="glass-strong relative p-10 md:p-16 overflow-hidden">
          <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
          <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-neon-cyan/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-neon-pink/10 blur-3xl pointer-events-none" />

          <div className="relative z-10 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="overline mb-4 text-neon-cyan flex items-center gap-2">
                <FileText size={14} /> // FREE_DOWNLOAD
              </div>
              <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-5">
                The Local Website Playbook.
              </h2>
              <p className="text-white/70 text-base md:text-lg mb-6 max-w-xl">
                A short, practical guide on what a website that actually books jobs looks like — page by page. Pulled directly from the framework we build for clients. <span className="text-white">Yours free.</span>
              </p>

              <ul className="space-y-3 mb-8">
                {BENEFITS.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-white/80 text-sm md:text-base">
                    <span className="w-5 h-5 mt-0.5 flex-shrink-0 bg-neon-cyan/10 border border-neon-cyan/50 flex items-center justify-center">
                      <CheckCircle2 size={11} className="text-neon-cyan" />
                    </span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>

              {sent ? (
                <div data-testid="lead-magnet-success" className="glass p-5 border-l-2 border-neon-lime flex items-center gap-3">
                  <CheckCircle2 className="text-neon-lime flex-shrink-0" size={20} />
                  <div>
                    <div className="font-heading font-bold text-base">Sent — check your inbox.</div>
                    <div className="text-white/50 text-sm">If you don&apos;t see it in 5 minutes, check spam or reply here.</div>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="lead-magnet-form">
                  <div className="relative flex-1">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="lead-magnet-email"
                      className="tsv-input !pl-10"
                      placeholder="your@email.com"
                      autoComplete="email"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="lead-magnet-submit"
                    className="btn-primary !bg-neon-cyan !border-neon-cyan !text-[#05050A] hover:!bg-transparent hover:!text-neon-cyan disabled:opacity-50"
                  >
                    {loading ? "Sending…" : "Send Me The Guide"} <Download size={14} />
                  </button>
                </form>
              )}

              <p className="font-mono text-[10px] uppercase tracking-widest text-white/30 mt-4">
                No spam. Unsubscribe anytime.
              </p>
            </div>

            <div className="lg:col-span-5 relative">
              <div className="glass border-l-2 border-neon-cyan relative p-7 md:p-8 transform rotate-1 hover:rotate-0 transition-transform duration-500">
                <div className="font-mono text-[10px] uppercase tracking-widest text-neon-cyan mb-3">
                  // .PDF · 18 PAGES
                </div>
                <h3 className="font-heading font-black text-2xl md:text-3xl leading-tight mb-4">
                  The Local Website Playbook
                </h3>
                <div className="font-mono text-[11px] text-white/50 uppercase tracking-wider mb-6">
                  Seven Pages That Actually Book Jobs
                </div>
                <div className="space-y-2 mb-6">
                  {["01 / Home", "02 / About", "03 / Services", "04 / Service Pages", "05 / Reviews", "06 / Contact", "07 / Thank You"].map((p) => (
                    <div key={p} className="font-mono text-[11px] text-white/40 border-b border-white/5 pb-1.5">
                      {p}
                    </div>
                  ))}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-white/30">
                  techsavvypreneurs.com
                </div>
              </div>
              <div className="absolute -bottom-3 -right-3 w-full h-full border border-neon-cyan/30 -z-10" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
